import React from 'react';
import classnames from 'classnames';
import { useIsMobile } from '@guildeducationinc/catalog-tools';
import { Typography } from '@guildeducationinc/recess';
import { institutionImageLink } from 'src/utilities/urlUtility';
import { GetMemberProgramQuery } from 'src/graphqlTypes/catalogServiceTypes';
import { AccreditedFeature } from 'src/components/ProgramDetails/Institution/About/AccreditedFeature';
import { NonProfitFeature } from 'src/components/ProgramDetails/Institution/About/NonProfitFeature';
import { InstitutionVideo } from 'src/components/ProgramDetails/Institution/About/InstitutionVideo';
import styles from './About.module.scss';

interface Props {
	program: GetMemberProgramQuery['memberProgram'];
	showImage: boolean;
	setShowImage: React.Dispatch<React.SetStateAction<boolean>>;
}

export const About: React.FC<Props> = ({ program, showImage, setShowImage }) => {
	const { studentFacingAcademicPartner } = program;
	const handleError = () => {
		setShowImage(false);
	};
	const institutionImageSrc = institutionImageLink(studentFacingAcademicPartner.informalName);

	const isMobile = useIsMobile();
	const isAccredited = studentFacingAcademicPartner.accredited;
	const isNonProfit = studentFacingAcademicPartner.nonProfit;

	if ((!institutionImageSrc || !showImage) && !studentFacingAcademicPartner.overview) {
		return null;
	}

	let imageContent = null;
	if (institutionImageSrc && showImage) {
		imageContent = (
			<div className={styles.imageContainer} data-testid='institution-image'>
				<img alt='' onError={handleError} src={institutionImageSrc} />
			</div>
		);
	}

	return (
		<section
			aria-label={`about ${studentFacingAcademicPartner.name}`}
			className={styles.institutionAboutContent}
		>
			<div className='flex'>
				{imageContent}
				<div>
					<Typography className={!isMobile && styles.institutionName} variant='h3'>
						{studentFacingAcademicPartner.name}
					</Typography>
					{isAccredited || isNonProfit ? (
						<div className={styles.features}>
							<AccreditedFeature isAccredited={isAccredited} />
							<NonProfitFeature isNonProfit={isNonProfit} />
						</div>
					) : null}
					<Typography className={styles.overview} variant={isMobile ? 'body1' : 'body2'}>
						{studentFacingAcademicPartner.overview}
					</Typography>
				</div>
			</div>
			<InstitutionVideo institution={studentFacingAcademicPartner} programId={program.id} />
		</section>
	);
};
